import { css } from '@inter-ikea-kompis/web-component';
import type { ITheme } from '@inter-ikea-kompis/types';

import ToastAnimationNameEnum from '../../enums/ToastAnimationNameEnum.js';

type Options = {
	isVisible: boolean;
	theme: ITheme;
	top: number;
	bottom: number;
	direction: string;
	isAnimating: boolean;
};

const MAX_WIDTH = 360;
const MIN_HEIGHT = 66;

export default class ToastStyle {
	/**
	 * Returns styles.
	 *
	 * @param options Options object.
	 * @param options.isVisible "true" if visible.
	 * @param options.theme Theme.
	 * @returns Styles.
	 */
	public static getStyle(options: Options): string {
		const { color, breakpoint, spacing } = options.theme;
		return css`
			:host {
				position: fixed;
				display: flex;
				flex-direction: column;
				z-index: 9999;
				top: ${options.top}px;
				bottom: ${options.bottom}px;
				left: 0;
				right: 0;
				transition-duration: ${options.theme.motion.duration.medium};
				transition-property: bottom, top;
				pointer-events: none;
			}
			.toastContainer {
				pointer-events: none;
				display: flex;
				animation-name: ${options.isVisible ? 'opening' : 'closing'};
				${this.getAnimationName(options)};
				animation-fill-mode: both;
				animation-duration: ${options.theme.motion.duration.medium};
				animation-timing-function: ${options.isVisible
					? options.theme.motion.ease.enter
					: options.theme.motion.ease.exit};
			}
			.toast {
				max-width: 100%;
				flex-grow: 1;
				display: flex;
				align-items: center;
				color: ${color.neutral.white};
				min-height: ${MIN_HEIGHT / 16}rem;
				padding: ${spacing.space100 / 16}rem;
				background-color: ${color.neutral.grey900};
				pointer-events: ${options.isVisible ? 'auto' : 'none'};
			}
			* {
				z-index: inherit;
			}
			.action {
				margin: 0 ${spacing.space50 / 16}rem;
			}
			.message {
				word-break: break-word;
				min-width: 50%;
				flex-basis: 0;
				flex-grow: 1;
				margin: 0 ${spacing.space50 / 16}rem;
			}
			.close {
				cursor: pointer;
				flex-shrink: 0;
			}
			.filler {
				flex-grow: 1;
			}
			.space-25 {
				flex-shrink: 0;
				width: ${spacing.space25 / 16}rem;
			}
			.stripe {
				align-self: stretch;
				flex-shrink: 0;
				width: ${1 / 16}rem;
				opacity: 0.2;
				background-color: ${color.neutral.white};
				margin: 0 ${spacing.space50 / 16}rem;
			}
			.body {
				flex-grow: 1;
				flex-shrink: 1;
				display: flex;
				flex-wrap: wrap;
				flex-basis: auto;
				align-items: center;
				justify-content: space-between;
			}
			.content {
				flex-basis: auto;
				display: inline;
			}
			/* Desktop */
			@media screen and (min-width: ${breakpoint.medium.width / 16}rem) {
				${this.getBannerStyle(options)}
			}
			/* Mobile */
			@media screen and (max-width: ${breakpoint.medium.width / 16}rem) {
				${this.getBarStyle()}
			}
		`;
	}

	private static getAnimationName(options: Options): string {
		if (!options.isAnimating) {
			return '';
		}

		if (options.isVisible) {
			return css`
				animation-name: ${ToastAnimationNameEnum.opening};
			`;
		}
		return css`
			animation-name: ${ToastAnimationNameEnum.closing};
		`;
	}

	private static getBannerStyle(options: Options): string {
		const { spacing, border } = options.theme;
		const translation = options.direction === 'rtl' ? '-100%' : '100%';
		return css`
			:host {
				align-items: flex-end;
			}
			.toast {
				width: ${MAX_WIDTH / 16}rem;
				border-radius: ${border.radius.radius4 / 16}rem;
			}
			.toastContainer {
				padding: ${spacing.space150 / 16}rem 0;
			}
			.containerSpace {
				flex-shrink: 0;
				width: ${spacing.space150 / 16}rem;
			}
			@keyframes closing {
				from {
					transform: translateX(0);
				}
				to {
					transform: translateX(${translation});
				}
			}
			@keyframes opening {
				from {
					transform: translateX(${translation});
				}
				to {
					transform: translateX(0);
				}
			}
		`;
	}

	private static getBarStyle(): string {
		return css`
			.toast {
				flex-grow: 1;
			}
			@keyframes closing {
				from {
					transform: translateY(0);
				}
				to {
					transform: translateY(-100%);
				}
			}
			@keyframes opening {
				from {
					transform: translateY(-100%);
				}
				to {
					transform: translateY(0);
				}
			}
		`;
	}
}
